import type { Course, CustomWeightingConfig } from '@gpa-scales/shared-types';
import { GradeLevel } from '@gpa-scales/shared-types';

/**
 * Apply custom weighting to a course, returning the bonus points.
 */
export function applyCustomWeighting(
  course: Course,
  config: CustomWeightingConfig
): number {
  const bonus = config.bonusPoints[course.courseType];
  if (bonus === undefined) return 0;
  return bonus;
}

/**
 * Apply custom point cap to a list of honors courses.
 * Returns the list of courses that count (within cap), and the number of capped points.
 */
export function applyCustomPointCap(
  honorsCourses: Course[],
  config: CustomWeightingConfig,
  _allCourses: Course[]
): { cappedCourses: Course[]; usedPoints: number } {
  if (!config.pointCap) {
    return { cappedCourses: honorsCourses, usedPoints: honorsCourses.length };
  }

  const maxHonors = config.pointCap.maxHonorsPoints;
  const maxPerGrade = config.pointCap.maxPerGradeLevel;

  // Apply grade-level sub-caps first
  let remaining = [...honorsCourses];
  const gradeCounts: Partial<Record<GradeLevel, number>> = {};

  if (maxPerGrade) {
    const afterGradeCap: Course[] = [];
    for (const course of remaining) {
      const gradeLimit = maxPerGrade[course.gradeLevel];
      if (gradeLimit !== undefined) {
        const currentCount = gradeCounts[course.gradeLevel] ?? 0;
        if (currentCount < gradeLimit) {
          afterGradeCap.push(course);
          gradeCounts[course.gradeLevel] = currentCount + 1;
        }
      } else {
        afterGradeCap.push(course);
      }
    }
    remaining = afterGradeCap;
  }

  // Apply total cap
  const cappedCourses = remaining.slice(0, maxHonors);
  const usedPoints = cappedCourses.length;

  return { cappedCourses, usedPoints };
}

/**
 * Validate a custom weighting config.
 * Returns an array of error messages (empty if valid).
 */
export function validateCustomConfig(config: CustomWeightingConfig): string[] {
  const errors: string[] = [];

  if (config.scaleMaximum < 4.0 || config.scaleMaximum > 10.0) {
    errors.push('Scale maximum must be between 4.0 and 10.0');
  }

  if (config.pointCap) {
    if (config.pointCap.maxHonorsPoints < 0) {
      errors.push('Max honors points cannot be negative');
    }

    if (config.pointCap.maxPerGradeLevel) {
      for (const [grade, limit] of Object.entries(config.pointCap.maxPerGradeLevel)) {
        if (limit !== undefined && limit < 0) {
          errors.push(`Grade ${grade} sub-cap cannot be negative`);
        }
      }
    }
  }

  // Validate bonus points are reasonable
  for (const [courseType, bonus] of Object.entries(config.bonusPoints)) {
    if (bonus !== undefined) {
      if (bonus < 0) {
        errors.push(`Bonus for ${courseType} cannot be negative`);
      }
      if (bonus > 3) {
        errors.push(`Bonus for ${courseType} seems unreasonably high (${bonus})`);
      }
    }
  }

  return errors;
}
